/**
 * ImageEditPlugin - 图片编辑插件（悬停编辑模式）
 *
 * 功能：
 * - 鼠标悬停图片时显示"编辑"按钮
 * - 点击编辑按钮将节点替换为可编辑面板
 * - 输入 /image 插入新的可编辑图片
 * - 保持在文本流中
 *
 * 实现方式：
 * - 使用 mouseenter/mouseleave 事件
 * - 不修改 Lexical 管理的 DOM 结构
 * - 使用 fixed 定位的编辑按钮（放在 body 中）
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createParagraphNode, $insertNodes } from "lexical";
import { useEffect, useRef } from "react";
import { $createEditableImageNode } from "../nodes/EditableImageNode";
import { ImageNode } from "../nodes/ImageNode";

export function ImageEditPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const imagesRef = useRef<Map<HTMLImageElement, string>>(new Map());
  const buttonsRef = useRef<Map<HTMLImageElement, HTMLButtonElement>>(new Map());

  // 暴露创建方法到 window（供 SlashCommand 调用）
  useEffect(() => {
    (window as any).editor = editor;

    (window as any).openImageCreateDialog = () => {
      editor.update(() => {
        const editableImageNode = $createEditableImageNode("", undefined, undefined, undefined);
        $insertNodes([editableImageNode]);

        const paragraphNode = $createParagraphNode();
        $insertNodes([paragraphNode]);
        paragraphNode.select(0, 0);
      });
    };
    return () => {
      delete (window as any).openImageCreateDialog;
    };
  }, [editor]);

  // 监听图片并添加事件
  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const setupImage = (imgElement: HTMLImageElement) => {
      if (imagesRef.current.has(imgElement)) return;

      // 找到包含 data-node-key 的 span
      const containerSpan = imgElement.closest("span[data-node-key]");
      if (!containerSpan) {
        return;
      }

      const nodeKey = containerSpan.getAttribute("data-node-key");
      if (!nodeKey) {
        return;
      }

      imagesRef.current.set(imgElement, nodeKey);

      let editBtn: HTMLButtonElement | null = null;
      let hideTimeout: number | null = null;

      const showButton = () => {
        if (editBtn) return;

        editBtn = document.createElement("button");
        editBtn.innerHTML = "✎";
        editBtn.title = "编辑图片";
        editBtn.type = "button";

        // 使用内联样式设置所有样式，避免 CSS 冲突
        Object.assign(editBtn.style, {
          position: "fixed",
          width: "28px",
          height: "28px",
          fontSize: "12px",
          cursor: "pointer",
          borderRadius: "4px",
          backgroundColor: "hsl(var(--primary))",
          color: "hsl(var(--primary-foreground))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          zIndex: "9999",
          pointerEvents: "auto",
          userSelect: "none",
          webkitUserSelect: "none",
          transition: "opacity 0.15s, transform 0.15s",
          opacity: "1",
        });

        // 添加 hover 效果
        editBtn.addEventListener("mouseenter", () => {
          if (editBtn) {
            editBtn.style.transform = "scale(1.1)";
            // 取消隐藏定时器
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
          }
        });
        editBtn.addEventListener("mouseleave", () => {
          if (editBtn) editBtn.style.transform = "scale(1)";
        });

        // 计算位置
        const rect = imgElement.getBoundingClientRect();
        editBtn.style.top = `${rect.top + rect.height / 2 - 14}px`;
        editBtn.style.left = `${rect.right + 8}px`;

        // 添加到 body（而不是 rootElement）
        document.body.appendChild(editBtn);
        buttonsRef.current.set(imgElement, editBtn);

        editBtn.addEventListener("click", (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();

          editor.getEditorState().read(() => {
            const nodeMap = editor.getEditorState()._nodeMap;
            const node = nodeMap.get(nodeKey);

            if (node instanceof ImageNode) {
              editor.update(() => {
                const editableNode = $createEditableImageNode(
                  node.getSrc(),
                  node.getAltText() || undefined,
                  node.getWidth() || undefined,
                  node.getHeight() || undefined,
                );
                node.replace(editableNode);
              });
            }
          });

          cleanupButton();
        });
      };

      const hideButton = () => {
        if (hideTimeout) clearTimeout(hideTimeout);
        hideTimeout = window.setTimeout(() => {
          // 只在鼠标不在按钮上时才移除
          if (editBtn && editBtn.matches(":hover")) {
            return;
          }
          cleanupButton();
        }, 200);
      };

      const cleanupButton = () => {
        if (editBtn && editBtn.parentElement) {
          editBtn.remove();
        }
        editBtn = null;
        buttonsRef.current.delete(imgElement);
      };

      imgElement.addEventListener("mouseenter", showButton);
      imgElement.addEventListener("mouseleave", hideButton);
    };

    // 初始设置所有图片
    const images = rootElement.querySelectorAll("img");
    images.forEach((img) => {
      if (img instanceof HTMLImageElement) {
        setupImage(img);
      }
    });

    // 监听新图片
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLImageElement) {
            setupImage(node);
          } else if (node instanceof Element) {
            const images = node.querySelectorAll("img");
            images.forEach((img) => {
              if (img instanceof HTMLImageElement) {
                setupImage(img);
              }
            });
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (node instanceof HTMLImageElement) {
            imagesRef.current.delete(node);
            const btn = buttonsRef.current.get(node);
            if (btn) btn.remove();
            buttonsRef.current.delete(node);
          }
        });
      });
    });

    observer.observe(rootElement, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      imagesRef.current.forEach((_, img) => {
        img.removeEventListener("mouseenter", () => {});
        img.removeEventListener("mouseleave", () => {});
      });
      imagesRef.current.clear();
      buttonsRef.current.forEach((btn) => btn.remove());
      buttonsRef.current.clear();
    };
  }, [editor]);

  return <></>;
}

export default ImageEditPlugin;
